import React from "react";
import { Link, useParams } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminTopNav } from "@/components/admin/AdminTopNav";
import { adminGet, adminPost } from "@/lib/admin-api";

interface DonorRecord {
  id: string;
  email: string;
  displayName: string;
  totalGiftsCount: number;
  totalGiftsAmountUsd: number;
  firstGiftAt: string | null;
  lastGiftAt: string | null;
  recognitionConsent: "anonymous" | "named" | "unspecified";
  recognitionDisplayName?: string;
  recognitionLockedAt?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
  preferredMemoTags?: string[];
  createdAt?: string;
}

interface Note {
  id: string;
  donorId: string;
  authorEmail: string;
  body: string;
  createdAt: string;
}

interface Gift {
  id: string;
  externalId: string;
  amountUsd: number;
  status: string;
  createdAt: string;
  metadata?: { dpfWishId?: string };
}

interface DetailPayload {
  donor: DonorRecord;
  notes: Note[];
  gifts: Gift[];
}

type Tab = "overview" | "gifts" | "notes";

function DonorDetailInner() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = React.useState<DetailPayload | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<Tab>("overview");
  const [noteDraft, setNoteDraft] = React.useState<string>("");
  const [savingNote, setSavingNote] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const res = await adminGet<DetailPayload>(`/api/admin/donors/get?id=${encodeURIComponent(id)}`);
      setData(res);
    } catch (err) {
      setError(String(err));
    }
  }, [id]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function onAddNote(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !noteDraft.trim()) return;
    setSavingNote(true);
    try {
      await adminPost("/api/admin/donors/note", { donorId: id, body: noteDraft.trim() });
      setNoteDraft("");
      await load();
    } catch (err) {
      setError(String(err));
    } finally {
      setSavingNote(false);
    }
  }

  return (
    <PageShell title="Donor · Admin" status="admin" width="wide">
      <Seo title="Donor detail" description="" path={`/admin/donors/${id ?? ""}`} noindex />
      <AdminTopNav />
      <div className="mb-6">
        <Link
          to="/admin/donors"
          className="font-mono text-[10px] uppercase tracking-widest text-ink-3 hover:text-ink"
        >
          ← All donors
        </Link>
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500 mb-6">
          {error}
        </div>
      )}

      {!data && !error && (
        <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">Loading…</p>
      )}

      {data && (
        <>
          <header className="mb-8">
            <h1 className="font-serif text-3xl text-ink mb-2 leading-tight">
              {data.donor.displayName || data.donor.email}
            </h1>
            <p className="font-mono text-xs text-ink-3">{data.donor.email}</p>
            <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm font-mono text-ink-2">
              <div>
                <span className="text-ink-3 uppercase tracking-widest text-[10px] mr-2">Total</span>
                <span className="text-ink">
                  US$
                  {data.donor.totalGiftsAmountUsd.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div>
                <span className="text-ink-3 uppercase tracking-widest text-[10px] mr-2">Gifts</span>
                <span className="text-ink">{data.donor.totalGiftsCount}</span>
              </div>
              <div>
                <span className="text-ink-3 uppercase tracking-widest text-[10px] mr-2">First gift</span>
                <span className="text-ink">
                  {data.donor.firstGiftAt
                    ? new Date(data.donor.firstGiftAt).toLocaleDateString("en-GB")
                    : "—"}
                </span>
              </div>
              <div>
                <span className="text-ink-3 uppercase tracking-widest text-[10px] mr-2">Recognition</span>
                <span className="text-claude">
                  {data.donor.recognitionConsent === "named"
                    ? data.donor.recognitionDisplayName || "Named"
                    : data.donor.recognitionConsent === "anonymous"
                      ? "Anonymous"
                      : "Unspecified"}
                </span>
              </div>
            </div>
            <p className="mt-3 font-mono text-[10px] text-ink-3 italic">
              Admin cannot override the donor&rsquo;s recognition preference.
            </p>
          </header>

          <nav className="flex gap-6 border-b border-hairline mb-6">
            {(["overview", "gifts", "notes"] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={
                  tab === t
                    ? "font-mono text-xs uppercase tracking-widest text-ink border-b-2 border-ink pb-2 -mb-px"
                    : "font-mono text-xs uppercase tracking-widest text-ink-3 hover:text-ink pb-2"
                }
              >
                {t}
                {t === "gifts" && ` (${data.gifts.length})`}
                {t === "notes" && ` (${data.notes.length})`}
              </button>
            ))}
          </nav>

          {tab === "overview" && (
            <section className="space-y-8">
              <div>
                <h3 className="font-serif text-lg text-ink mb-3">Recent activity</h3>
                {data.gifts.length === 0 && data.notes.length === 0 ? (
                  <p className="font-serif text-sm text-ink-2">No activity yet.</p>
                ) : (
                  <ul className="divide-y divide-hairline border-y border-hairline">
                    {[...data.gifts.slice(0, 5).map((g) => ({ kind: "gift" as const, ts: g.createdAt, payload: g })), ...data.notes.slice(0, 5).map((n) => ({ kind: "note" as const, ts: n.createdAt, payload: n }))]
                      .sort((a, b) => (a.ts < b.ts ? 1 : -1))
                      .slice(0, 8)
                      .map((entry, i) => (
                        <li key={i} className="py-3 text-sm">
                          <span className="font-mono text-[10px] text-ink-3 uppercase tracking-widest mr-2">
                            {new Date(entry.ts).toLocaleDateString("en-GB")}
                          </span>
                          {entry.kind === "gift" ? (
                            <>
                              Gift —{" "}
                              <span className="font-mono text-ink">
                                US${entry.payload.amountUsd.toLocaleString("en-US")}
                              </span>{" "}
                              ({entry.payload.metadata?.dpfWishId || "general"})
                            </>
                          ) : (
                            <>Note — {entry.payload.body.slice(0, 120)}</>
                          )}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="font-serif text-lg text-ink mb-3">Address</h3>
                <p className="font-mono text-sm text-ink-2 leading-relaxed">
                  {data.donor.address?.line1 ? (
                    <>
                      {data.donor.address.line1}
                      {data.donor.address.line2 ? <><br />{data.donor.address.line2}</> : null}
                      <br />
                      {data.donor.address.city ?? ""}
                      {data.donor.address.state ? `, ${data.donor.address.state}` : ""}
                      {" "}
                      {data.donor.address.postcode ?? ""}
                      <br />
                      {data.donor.address.country ?? ""}
                    </>
                  ) : (
                    <span className="text-ink-3">No address on file.</span>
                  )}
                </p>
              </div>
            </section>
          )}

          {tab === "gifts" && (
            <section>
              {data.gifts.length === 0 ? (
                <p className="font-serif text-sm text-ink-2">No gifts yet.</p>
              ) : (
                <div className="border border-hairline overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-paper-2 text-[10px] font-mono uppercase tracking-widest text-ink-3">
                      <tr className="border-b border-hairline">
                        <th className="text-left px-4 py-3 font-bold">Date</th>
                        <th className="text-left px-4 py-3 font-bold">Wish item</th>
                        <th className="text-right px-4 py-3 font-bold">Amount</th>
                        <th className="text-left px-4 py-3 font-bold">Status</th>
                        <th className="text-left px-4 py-3 font-bold">Stripe pi_</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-hairline text-ink-2">
                      {data.gifts.map((g) => (
                        <tr key={g.id}>
                          <td className="px-4 py-3 font-mono text-xs">
                            {new Date(g.createdAt).toLocaleDateString("en-GB")}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {g.metadata?.dpfWishId || "general"}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-right">
                            US${g.amountUsd.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest">
                            {g.status}
                          </td>
                          <td className="px-4 py-3 font-mono text-[10px] text-ink-3">
                            {g.externalId.slice(0, 14)}…
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {tab === "notes" && (
            <section className="space-y-6">
              <form onSubmit={onAddNote} className="space-y-3">
                <textarea
                  rows={3}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Internal note (visible to admins only)"
                  disabled={savingNote}
                  className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm text-ink focus:outline-none focus:border-ink"
                />
                <button
                  type="submit"
                  disabled={savingNote || !noteDraft.trim()}
                  className="bg-ink text-paper px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-ink-2 disabled:opacity-60"
                >
                  {savingNote ? "Saving…" : "Add note"}
                </button>
              </form>
              {data.notes.length === 0 ? (
                <p className="font-serif text-sm text-ink-2">No notes yet.</p>
              ) : (
                <ul className="divide-y divide-hairline border-y border-hairline">
                  {data.notes.map((n) => (
                    <li key={n.id} className="py-4">
                      <div className="font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-1">
                        {new Date(n.createdAt).toLocaleString("en-GB")} · {n.authorEmail}
                      </div>
                      <p className="font-serif text-sm text-ink-2 leading-relaxed whitespace-pre-wrap">
                        {n.body}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
    </PageShell>
  );
}

export function DonorDetail() {
  return (
    <AdminGuard allow={["admin"]}>
      <DonorDetailInner />
    </AdminGuard>
  );
}
